import React from 'react'
import { Button, ChatToolCard } from '@/components'
import type { ToolCardStatus, ToolConfigEntry } from '@/components'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'
import LongRunNotice from './LongRunNotice'
import { summaryLine, type WalkthroughToolData } from './walkthroughSummary'

export type { WalkthroughMatching, WalkthroughToolData } from './walkthroughSummary'

/**
 * The run_walkthrough tool card: the chat's Auto-verify loop replaying the generated
 * code over the user's drawings, one automatic round at a time.
 *
 * Mirrors BotMessageToolBacktest's shape (stage text while running, a summary once
 * done) with one difference in the done state: instead of a results badge it says how
 * many of the drawn trade ideas the code reproduced, and offers to open walkthrough
 * mode — the same place the "Run walkthrough test" button leads.
 */

interface BotMessageToolWalkthroughProps {
  toolData: WalkthroughToolData
  status?: MessageStatus
  msgId?: string
  /** "View walkthrough" — enters the Create tab's walkthrough mode. */
  onViewWalkthrough?: () => void
  /**
   * Render the "View walkthrough" button. The caller decides, because the button opens
   * walkthrough mode for the CURRENT code: it belongs on the latest finished run only,
   * once the chat is idle — not on every round's card as the loop goes.
   */
  showViewButton?: boolean
}

// Live stage labels (only shown while running). Driven by real SSE toolStatus
// updates, not synthetic cycling.
const RUNNING_STAGES: Record<string, string> = {
  WALKTHROUGH_STARTED: 'Starting',
  WALKTHROUGH_CONFIGURING: 'Configuring the run',
  WALKTHROUGH_QUEUED: 'Waiting for a runner',
  WALKTHROUGH_RUNNING: 'Replaying your drawings',
  WALKTHROUGH_ANALYZING: 'Comparing orders with your drawings',
}

/** Outcomes that never ran — they get their own label rather than "(Failed)". */
const NOT_RUN_LABELS: Record<string, string> = {
  WALKTHROUGH_NO_DRAWINGS: 'No drawings to verify against',
  WALKTHROUGH_NO_DATA: 'No price data for the drawn period',
  WALKTHROUGH_BLOCKED: 'Verification needs AI usage',
}

export default function BotMessageToolWalkthrough({
  toolData,
  status,
  msgId,
  onViewWalkthrough,
  showViewButton = false,
}: BotMessageToolWalkthroughProps) {
  const cardStatus = resolveStatus(status, toolData.status, toolData.toolStatus)
  const elapsedTime = useElapsedTime(toolData.startedAt, toolData.completedAt, cardStatus === 'running')

  const stage = cardStatus === 'running' ? RUNNING_STAGES[toolData.toolStatus ?? ''] : undefined
  const config = buildConfig(toolData, cardStatus)
  const summary = cardStatus === 'done' ? summaryLine(toolData) : null

  return (
    <div className="space-y-2" data-msg-id={msgId}>
      <ChatToolCard
        orb="walkthrough"
        message={{
          id: 0,
          type: 'tool',
          label: labelFor(toolData, cardStatus),
          status: cardStatus,
          stage,
          duration: cardStatus === 'done' || cardStatus === 'error' ? formatTime(elapsedTime) : undefined,
          config,
        }}
      />
      {msgId && <LongRunNotice noticeId={msgId} startedAt={toolData.startedAt} running={cardStatus === 'running'} />}
      {cardStatus === 'done' && toolData.btid && (summary || (showViewButton && onViewWalkthrough)) && (
        <div className="flex flex-wrap items-center gap-2 pl-5 text-caption text-muted-foreground">
          {summary && <span>{summary}</span>}
          {showViewButton && onViewWalkthrough && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-label"
              onClick={onViewWalkthrough}
            >
              View walkthrough
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function labelFor(toolData: WalkthroughToolData, status: ToolCardStatus) {
  const notRun = NOT_RUN_LABELS[toolData.toolStatus ?? '']
  if (notRun) return notRun

  // A run the user asked for is not a verification round, so it is not labelled as one.
  const requested = toolData.autoRun === false
  const round =
    !requested && toolData.round != null && toolData.maxRounds != null
      ? ` · round ${toolData.round} of ${toolData.maxRounds}`
      : ''
  const base = requested ? 'Walkthrough' : `Verifying against your chart${round}`
  if (status === 'canceled') return `${base} (Canceled)`
  if (status === 'error') return `${base} (Failed)`
  return base
}

function resolveStatus(
  status: MessageStatus | undefined,
  toolStatus: WalkthroughToolData['status'],
  serverStatus: string | undefined,
): ToolCardStatus {
  // Outcomes that never ran are written PROCESSED (a FAILED row locks the composer), so
  // they must be read off the tool's own status ahead of the PROCESSED branch below.
  if (serverStatus === 'WALKTHROUGH_CANCELED' || status === MESSAGE_STATUS.CANCELED || toolStatus === 'canceled') {
    return 'canceled'
  }
  if (
    status === MESSAGE_STATUS.FAILED ||
    toolStatus === 'error' ||
    toolStatus === 'timeout' ||
    serverStatus === 'WALKTHROUGH_ERROR' ||
    serverStatus === 'WALKTHROUGH_TIMEOUT' ||
    serverStatus === 'WALKTHROUGH_NO_DATA' ||
    serverStatus === 'WALKTHROUGH_BLOCKED' ||
    serverStatus === 'WALKTHROUGH_NO_DRAWINGS'
  ) {
    return 'error'
  }
  if (
    status === MESSAGE_STATUS.PROCESSED ||
    toolStatus === 'completed' ||
    serverStatus === 'WALKTHROUGH_COMPLETED'
  ) {
    return 'done'
  }
  return 'running'
}

function buildConfig(
  toolData: WalkthroughToolData,
  cardStatus: ToolCardStatus,
): ToolConfigEntry[] | undefined {
  const entries: ToolConfigEntry[] = []
  const wt = toolData.walkthrough

  if (toolData.toolInput?.reason) {
    entries.push({ label: 'Checking', value: toolData.toolInput.reason })
  }
  if (wt?.symbol) entries.push({ label: 'Symbol', value: wt.symbol })
  if (wt?.timeframe) entries.push({ label: 'Timeframe', value: wt.timeframe })
  if (wt?.period?.startDate || wt?.period?.endDate) {
    entries.push({ label: 'Period', value: `${wt.period?.startDate ?? ''} – ${wt.period?.endDate ?? ''}` })
  }
  if (wt?.warmupBars != null) {
    entries.push({
      label: 'Warm-up',
      value: wt.lookback ? `${wt.warmupBars} bars (for a ${wt.lookback}-bar lookback)` : `${wt.warmupBars} bars`,
    })
  }
  if (cardStatus === 'done' && (toolData.codeVersion != null || toolData.version != null)) {
    const version = formatVersion(toolData.codeVersion, toolData.version)
    if (version) entries.push({ label: 'Version', value: <span className="font-mono tabular-nums">{version}</span> })
  }
  if (cardStatus === 'error' && toolData.error) {
    entries.push({ label: 'Error', value: toolData.error })
  }

  return entries.length > 0 ? entries : undefined
}

function useElapsedTime(startedAt: string | undefined, completedAt: string | undefined, isRunning: boolean) {
  const [tickElapsed, setTickElapsed] = React.useState(0)

  React.useEffect(() => {
    if (!startedAt || !isRunning) return
    const start = new Date(startedAt).getTime()
    const interval = setInterval(() => {
      setTickElapsed((Date.now() - start) / 1000)
    }, 100)
    return () => clearInterval(interval)
  }, [startedAt, isRunning])

  if (!isRunning && startedAt && completedAt) {
    const start = new Date(startedAt).getTime()
    const end = new Date(completedAt).getTime()
    return (end - start) / 1000
  }
  return tickElapsed
}

function formatTime(seconds: number) {
  return `${seconds.toFixed(1)}s`
}

function formatVersion(codeVersion?: number, version?: number) {
  if (codeVersion != null && version != null) return `v${codeVersion}.${version}`
  if (version != null) return `v${version}`
  if (codeVersion != null) return `v${codeVersion}`
  return null
}
